import { useAuth } from './AuthProvider'

/**
 * Why the product project refused a read, in words a user can act on — 23 Oct 2026.
 *
 * The product project lets a CRM user read only from a verified
 * `@mountainsnow.ski` Google account. So a refusal almost always means one of
 * three things, and the fix differs: nothing is connected, the wrong account is
 * connected, or the right one is and the product's rules still say no. This
 * says which, names the account, and offers the button that fixes the first two.
 * The third is not fixable from here — those rules belong to the product.
 */
const ALLOWED = /@mountainsnow\.ski$/i

export function ProductAccessError({ what, error }: { what: string; error: string }) {
  const { productConnected, productEmail, connectProductData, productError } = useAuth()
  const refused = /permission|insufficient|unauthenticated/i.test(error)

  let reason: string
  let button: { label: string; choose: boolean } | null = null
  if (!refused) {
    reason = error
  } else if (!productConnected) {
    reason =
      'Product data is not connected. It lives in the product’s own Firebase project and needs its own Google sign-in.'
    button = { label: 'Connect product data', choose: false }
  } else if (!productEmail || !ALLOWED.test(productEmail)) {
    reason = `Product data is connected as ${productEmail ?? 'an account without an email address'}. Only a verified @mountainsnow.ski Google account can read it.`
    button = { label: 'Connect with another account', choose: true }
  } else {
    reason = `Connected as ${productEmail}, and the product project still refused. That account may not be verified, or the product’s rules have changed — ask an administrator.`
    button = { label: 'Connect again', choose: true }
  }

  return (
    <div className="workspace-error">
      <strong>Could not read {what}.</strong>
      <p>{reason}</p>
      {button && (
        <button className="btn btn--primary btn--tiny" onClick={() => void connectProductData(button.choose)}>
          {button.label}
        </button>
      )}
      {productError && <p className="micro">{productError}</p>}
      {refused && <p className="micro muted">The product project answered: {error}</p>}
    </div>
  )
}
